import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiMail,
  FiMapPin,
  FiMessageSquare,
  FiPhone,
} from 'react-icons/fi';

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const product = params.get('product');

    if (product) {
      setFormData((previous) => ({
        ...previous,
        subject: `Enquiry about: ${product}`,
      }));
    }
  }, [location]);

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.id]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsSubmitted(true);
  };

  return (
    <div className="bg-brand-cream min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-brand-charcoal mb-4">
            Contact DSGlobal Foundation
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We are here to assist you with product enquiries, wholesale orders, and general questions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 space-y-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-sand">
              <h3 className="text-xl font-bold text-brand-charcoal mb-6 border-b border-gray-100 pb-4">
                Get in Touch
              </h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-brand-sand rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <SafeIcon icon={FiMail} className="text-brand-green" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-charcoal mb-1">Email</p>
                    <a
                      href="mailto:naajoboat@gmail.com"
                      className="text-gray-600 hover:text-brand-green transition-colors"
                    >
                      naajoboat@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-brand-sand rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <SafeIcon icon={FiPhone} className="text-brand-green" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-charcoal mb-1">
                      Phone / WhatsApp
                    </p>
                    <a
                      href="tel:+46728373384"
                      className="text-gray-600 hover:text-brand-green transition-colors"
                    >
                      +46 72-837 33 84
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-brand-sand rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                    <SafeIcon icon={FiMapPin} className="text-brand-green" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-charcoal mb-1">Address</p>
                    <p className="text-gray-600">
                      FOSIEVÄGEN 27B
                      <br />
                      21431 MALMÖ
                      <br />
                      SWEDEN
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brand-green text-white p-8 rounded-2xl shadow-md relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 bg-pattern pointer-events-none" />
              <div className="relative z-10">
                <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-brand-yellow mb-4" />
                <h3 className="text-lg font-bold mb-2">Wholesale Notice</h3>
                <p className="text-sm text-gray-200 leading-relaxed">
                  For wholesale orders, please include product names, estimated quantities and your destination country in your message to help us serve you better.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-brand-sand">
              <h2 className="text-2xl font-serif font-bold text-brand-charcoal mb-8">
                Send a Message
              </h2>

              {isSubmitted ? (
                <div className="text-center py-12 bg-green-50 rounded-xl border border-green-100">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-green-600 text-2xl">✓</span>
                  </div>
                  <h3 className="text-2xl font-bold text-brand-charcoal mb-2">Message Sent</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for contacting us. We will reply as soon as possible.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-brand-green font-semibold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-brand-green focus:border-brand-green outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-brand-green focus:border-brand-green outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-brand-green focus:border-brand-green outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-brand-green focus:border-brand-green outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-brand-green focus:border-brand-green outline-none transition-colors"
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      className="px-8 py-4 bg-brand-charcoal text-white font-bold rounded-md hover:bg-opacity-90 transition-colors shadow-md min-w-[200px]"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;